import { ShieldAlert } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { MultiSelect } from "@/components/MultiSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type SecurityAction = "none" | "delete" | "warn" | "timeout" | "kick" | "ban";
type NukeAction = "none" | "kick" | "ban";
type RaidAction = "alert" | "timeout_burst" | "kick_burst";

type Props = {
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onChange: (v: GuildConfig["security"]) => void;
};

const actionLabels: Record<SecurityAction, string> = {
  none: "Only log",
  delete: "Delete message",
  warn: "Warn",
  timeout: "Timeout",
  kick: "Kick",
  ban: "Ban",
};

function ActionSelect({
  value,
  onChange,
  allowDelete = true,
}: {
  value: SecurityAction;
  onChange: (v: SecurityAction) => void;
  allowDelete?: boolean;
}) {
  const values: SecurityAction[] = allowDelete
    ? ["none", "delete", "warn", "timeout", "kick", "ban"]
    : ["none", "warn", "timeout", "kick", "ban"];
  return (
    <Select value={value} onValueChange={(v) => onChange(v as SecurityAction)}>
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {values.map((v) => (
          <SelectItem key={v} value={v}>
            {actionLabels[v]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function RaidActionSelect({
  value,
  onChange,
}: {
  value: RaidAction;
  onChange: (v: RaidAction) => void;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as RaidAction)}>
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="alert">Log only</SelectItem>
        <SelectItem value="timeout_burst">Timeout join burst</SelectItem>
        <SelectItem value="kick_burst">Kick join burst</SelectItem>
      </SelectContent>
    </Select>
  );
}

function NukeActionSelect({
  value,
  onChange,
}: {
  value: NukeAction;
  onChange: (v: NukeAction) => void;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as NukeAction)}>
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Only log</SelectItem>
        <SelectItem value="kick">Kick executor</SelectItem>
        <SelectItem value="ban">Ban executor</SelectItem>
      </SelectContent>
    </Select>
  );
}

export function SecurityTab({ config, channels, roles, onChange }: Props) {
  const cfg = config.security;
  const textChannels = channels.filter((ch) => ch.type === 0);
  const channelOptions = channels.filter((ch) => ch.type === 0 || ch.type === 5);
  const update = <K extends keyof typeof cfg>(k: K, v: (typeof cfg)[K]) =>
    onChange({ ...cfg, [k]: v });
  const updateSpam = <K extends keyof typeof cfg.anti_spam>(
    k: K,
    v: (typeof cfg.anti_spam)[K],
  ) => onChange({ ...cfg, anti_spam: { ...cfg.anti_spam, [k]: v } });
  const updateInvite = <K extends keyof typeof cfg.anti_invite>(
    k: K,
    v: (typeof cfg.anti_invite)[K],
  ) => onChange({ ...cfg, anti_invite: { ...cfg.anti_invite, [k]: v } });
  const updateAutomod = <K extends keyof typeof cfg.automod>(
    k: K,
    v: (typeof cfg.automod)[K],
  ) => onChange({ ...cfg, automod: { ...cfg.automod, [k]: v } });
  const updateNuke = <K extends keyof typeof cfg.anti_nuke>(
    k: K,
    v: (typeof cfg.anti_nuke)[K],
  ) => onChange({ ...cfg, anti_nuke: { ...cfg.anti_nuke, [k]: v } });
  const updateRaid = <K extends keyof typeof cfg.anti_raid>(
    k: K,
    v: (typeof cfg.anti_raid)[K],
  ) => onChange({ ...cfg, anti_raid: { ...cfg.anti_raid, [k]: v } });
  const updateVerification = <K extends keyof typeof cfg.verification>(
    k: K,
    v: (typeof cfg.verification)[K],
  ) => onChange({ ...cfg, verification: { ...cfg.verification, [k]: v } });
  const updateTrapChannel = <K extends keyof typeof cfg.trap_channel>(
    k: K,
    v: (typeof cfg.trap_channel)[K],
  ) => onChange({ ...cfg, trap_channel: { ...cfg.trap_channel, [k]: v } });
  const updateLink = (k: string, v: any) => {
    onChange({
      ...cfg,
      anti_link: {
        enabled: false,
        action: "delete",
        whitelisted_domains: [],
        ...cfg.anti_link,
        [k]: v,
      },
    });
  };
  const roleOptions = roles.map((r) => ({ id: r.id, name: r.name, color: r.color }));
  const parseLines = (value: string, limit: number) =>
    value
      .split(/\r?\n/)
      .map((v) => v.trim())
      .filter(Boolean)
      .slice(0, limit);

  const parseDomains = (value: string, limit: number) =>
    value
      .split(/[\r\n\s,]+/)
      .map((v) => v.trim())
      .filter(Boolean)
      .slice(0, limit);

  return (
    <Section
      icon={ShieldAlert}
      title="Security"
      description="Anti-spam, invites, automod, anti-nuke, mass-join (raid) alerts, and member verification — similar to dedicated security bots."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          With Security enabled, protections run <strong>in the background</strong>: scanning messages for spam,
          invites and blocked words; watching audit logs for anti-nuke; counting joins for anti-raid; optionally
          assigning verification roles on join. No chat commands are required for those behaviors.
        </p>
        <p>
          Set a <strong>security log channel</strong> below to receive alerts. Use <strong>Never punish roles</strong>{" "}
          for staff bypass. For verification, configure roles here, then post the verify button in Discord using the
          slash command in the card below.
        </p>
      </div>

      <Card className="p-4 bg-muted/30">
        <h4 className="font-semibold mb-2 text-sm">Slash commands</h4>
        <div className="grid gap-1.5 text-sm">
          <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2">
            <code className="text-primary font-mono text-xs shrink-0">/verification_panel</code>
            <span className="text-xs text-muted-foreground">
              Posts an embed with a persistent <strong>Verify</strong> button (requires Manage Server or a security
              manager role). Set verified / unverified roles under Verification first.
            </span>
          </div>
        </div>
      </Card>

      <div className="rounded-lg border border-border p-4 space-y-4">
        <div>
          <div className="text-sm font-semibold">Security access</div>
          <div className="text-xs text-muted-foreground">
            Choose who can manage this security module and who is never punished by automatic actions.
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Security manager roles" hint="These roles can open/save Security settings.">
            <MultiSelect
              options={roles}
              value={cfg.manager_role_ids}
              onChange={(v) => update("manager_role_ids", v)}
              prefix="@"
              placeholder="+ add manager role"
            />
          </Field>
          <Field label="Never punish roles" hint="Bot will never warn/timeout/kick/ban these roles.">
            <MultiSelect
              options={roles}
              value={cfg.exempt_role_ids}
              onChange={(v) => update("exempt_role_ids", v)}
              prefix="@"
              placeholder="+ add bypass role"
            />
          </Field>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Security log channel">
          <ChannelPicker
            channels={textChannels}
            value={cfg.log_channel_id}
            onChange={(v) => update("log_channel_id", v === "__none__" ? "" : v)}
          />
        </Field>
        <Field label="Quick action" hint="Security managers can tune rules below for each buyer/server.">
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
            Automatic actions respect bypass roles/users first.
          </div>
        </Field>
      </div>

      <Field label="Ignored channels" hint="Anti-spam and invite checks do not run in these channels.">
        <MultiSelect
          options={channelOptions}
          value={cfg.exempt_channel_ids}
          onChange={(v) => update("exempt_channel_ids", v)}
          prefix="#"
          placeholder="+ add ignored channel"
        />
      </Field>

      <div className="rounded-lg border border-border p-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold">Anti-spam</div>
            <div className="text-xs text-muted-foreground">Detect repeated messages in a short time window.</div>
          </div>
          <Switch checked={cfg.anti_spam.enabled} onCheckedChange={(v) => updateSpam("enabled", v)} />
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          <Field label="Max messages">
            <Input type="number" min={3} max={30} value={cfg.anti_spam.max_messages} onChange={(e) => updateSpam("max_messages", Math.max(3, Number(e.target.value) || 6))} />
          </Field>
          <Field label="Seconds">
            <Input type="number" min={3} max={120} value={cfg.anti_spam.interval_seconds} onChange={(e) => updateSpam("interval_seconds", Math.max(3, Number(e.target.value) || 8))} />
          </Field>
          <Field label="Action">
            <ActionSelect value={cfg.anti_spam.action} onChange={(v) => updateSpam("action", v)} />
          </Field>
          <Field label="Timeout minutes">
            <Input type="number" min={1} max={10080} value={cfg.anti_spam.timeout_minutes} onChange={(e) => updateSpam("timeout_minutes", Math.max(1, Number(e.target.value) || 10))} />
          </Field>
          <Field label="Mention limit">
            <Input type="number" min={1} max={50} value={cfg.anti_spam.mention_limit} onChange={(e) => updateSpam("mention_limit", Math.max(1, Number(e.target.value) || 8))} />
          </Field>
          <Field label="Duplicate threshold">
            <Input type="number" min={2} max={20} value={cfg.anti_spam.duplicate_threshold} onChange={(e) => updateSpam("duplicate_threshold", Math.max(2, Number(e.target.value) || 4))} />
          </Field>
          <Field label="Caps percent">
            <Input type="number" min={50} max={100} value={cfg.anti_spam.caps_percent} onChange={(e) => updateSpam("caps_percent", Math.max(50, Number(e.target.value) || 80))} />
          </Field>
          <Field label="Caps min length">
            <Input type="number" min={5} max={500} value={cfg.anti_spam.caps_min_length} onChange={(e) => updateSpam("caps_min_length", Math.max(5, Number(e.target.value) || 20))} />
          </Field>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <div className="text-sm font-medium">Delete spam messages</div>
            <div className="text-xs text-muted-foreground">Remove the message that triggered the spam rule.</div>
          </div>
          <Switch checked={cfg.anti_spam.delete_messages} onCheckedChange={(v) => updateSpam("delete_messages", v)} />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <div className="text-sm font-medium">Detect duplicates</div>
            <div className="text-xs text-muted-foreground">Catch repeated same text.</div>
          </div>
          <Switch checked={cfg.anti_spam.duplicate_enabled} onCheckedChange={(v) => updateSpam("duplicate_enabled", v)} />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <div className="text-sm font-medium">Detect caps spam</div>
            <div className="text-xs text-muted-foreground">Catch excessive uppercase messages.</div>
          </div>
          <Switch checked={cfg.anti_spam.caps_enabled} onCheckedChange={(v) => updateSpam("caps_enabled", v)} />
        </div>
        </div>
      </div>

      <div className="rounded-lg border border-border p-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold">Automod words</div>
            <div className="text-xs text-muted-foreground">
              Delete and punish messages that contain words or patterns configured by the buyer.
            </div>
          </div>
          <Switch checked={cfg.automod.enabled} onCheckedChange={(v) => updateAutomod("enabled", v)} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Blocked words" hint="One word/phrase per line.">
            <Textarea
              rows={7}
              value={cfg.automod.blocked_words.join("\n")}
              onChange={(e) => updateAutomod("blocked_words", parseLines(e.target.value, 300))}
              placeholder={"badword\nbad phrase"}
            />
          </Field>
          <Field label="Blocked regex patterns" hint="Advanced: one regex per line. Invalid patterns are ignored.">
            <Textarea
              rows={7}
              value={cfg.automod.blocked_patterns.join("\n")}
              onChange={(e) => updateAutomod("blocked_patterns", parseLines(e.target.value, 100))}
              placeholder={"b[a@]dw[o0]rd\nhttps?://.+\\.ru"}
            />
          </Field>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          <Field label="Match mode">
            <Select value={cfg.automod.match_mode} onValueChange={(v) => updateAutomod("match_mode", v as "contains" | "word")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contains">Contains phrase</SelectItem>
                <SelectItem value="word">Whole word</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Action">
            <ActionSelect value={cfg.automod.action} onChange={(v) => updateAutomod("action", v)} />
          </Field>
          <Field label="Timeout minutes">
            <Input type="number" min={1} max={10080} value={cfg.automod.timeout_minutes} onChange={(e) => updateAutomod("timeout_minutes", Math.max(1, Number(e.target.value) || 10))} />
          </Field>
          <div className="grid gap-3">
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div className="text-sm font-medium">Delete message</div>
              <Switch checked={cfg.automod.delete_message} onCheckedChange={(v) => updateAutomod("delete_message", v)} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div className="text-sm font-medium">Case sensitive</div>
              <Switch checked={cfg.automod.case_sensitive} onCheckedChange={(v) => updateAutomod("case_sensitive", v)} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div className="text-sm font-medium">DM user</div>
              <Switch checked={cfg.automod.dm_user} onCheckedChange={(v) => updateAutomod("dm_user", v)} />
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border p-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold">Anti-invite links</div>
            <div className="text-xs text-muted-foreground">Block Discord invite links from normal members.</div>
          </div>
          <Switch checked={cfg.anti_invite.enabled} onCheckedChange={(v) => updateInvite("enabled", v)} />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Action">
            <ActionSelect value={cfg.anti_invite.action} onChange={(v) => updateInvite("action", v)} />
          </Field>
          <div className="flex items-center justify-between rounded-lg border border-border p-3 md:col-span-1">
            <div className="text-sm font-medium">Delete invite message</div>
            <Switch checked={cfg.anti_invite.delete_message} onCheckedChange={(v) => updateInvite("delete_message", v)} />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3 md:col-span-1">
            <div className="text-sm font-medium">Allow own server invites</div>
            <Switch checked={cfg.anti_invite.allow_own_invites} onCheckedChange={(v) => updateInvite("allow_own_invites", v)} />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border p-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold">Trap channel</div>
            <div className="text-xs text-muted-foreground">
              Anyone who sends a message in one of these channels is instantly timed out — a honeypot for
              hacked accounts and bots. Administrators (and Security manager / Never punish roles above) always bypass this.
            </div>
          </div>
          <Switch checked={cfg.trap_channel.enabled} onCheckedChange={(v) => updateTrapChannel("enabled", v)} />
        </div>
        {cfg.trap_channel.enabled && !cfg.enabled && (
          <p className="text-xs text-amber-500 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2">
            ⚠ The main <strong>Security</strong> toggle at the top of this page is off, so nothing here runs yet —
            including this trap channel. Turn Security on above too.
          </p>
        )}
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Trap channels" hint="Any message sent here triggers the timeout." >
            <MultiSelect
              options={channelOptions}
              value={cfg.trap_channel.channel_ids}
              onChange={(v) => updateTrapChannel("channel_ids", v)}
              prefix="#"
              placeholder="+ add trap channel"
            />
          </Field>
          <Field label="Timeout minutes" hint="Discord's max timeout is 10080 minutes (7 days).">
            <Input
              type="number"
              min={1}
              max={10080}
              value={cfg.trap_channel.timeout_minutes}
              onChange={(e) => updateTrapChannel("timeout_minutes", Math.max(1, Math.min(10080, Number(e.target.value) || 10080)))}
            />
          </Field>
        </div>
      </div>

      <div className="rounded-lg border border-border p-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold">Anti-raid (mass joins)</div>
            <div className="text-xs text-muted-foreground">
              Count joins in a short window; log or punish the burst when the threshold is exceeded (raid-style detection).
            </div>
          </div>
          <Switch checked={cfg.anti_raid.enabled} onCheckedChange={(v) => updateRaid("enabled", v)} />
        </div>
        <div className="grid md:grid-cols-6 gap-4">
          <Field label="Window (seconds)">
            <Input
              type="number"
              min={5}
              max={120}
              value={cfg.anti_raid.window_seconds}
              onChange={(e) => updateRaid("window_seconds", Math.max(5, Number(e.target.value) || 10))}
            />
          </Field>
          <Field label="Max joins">
            <Input
              type="number"
              min={3}
              max={100}
              value={cfg.anti_raid.max_joins}
              onChange={(e) => updateRaid("max_joins", Math.max(3, Number(e.target.value) || 10))}
            />
          </Field>
          <Field label="Alert cooldown (s)" hint="Avoid spamming the log when raids continue.">
            <Input
              type="number"
              min={30}
              max={3600}
              value={cfg.anti_raid.cooldown_seconds}
              onChange={(e) => updateRaid("cooldown_seconds", Math.max(30, Number(e.target.value) || 120))}
            />
          </Field>
          <Field label="Action">
            <RaidActionSelect value={cfg.anti_raid.action} onChange={(v) => updateRaid("action", v)} />
          </Field>
          <Field label="Timeout minutes" hint="Used when action is timeout burst.">
            <Input
              type="number"
              min={1}
              max={10080}
              value={cfg.anti_raid.timeout_minutes}
              onChange={(e) => updateRaid("timeout_minutes", Math.max(1, Number(e.target.value) || 60))}
            />
          </Field>
          <Field label="Only new accounts (min)" hint="0 = every user in burst; otherwise only accounts newer than this many minutes (approximate raid alts).">
            <Input
              type="number"
              min={0}
              max={43200}
              value={cfg.anti_raid.min_account_age_minutes}
              onChange={(e) =>
                updateRaid("min_account_age_minutes", Math.max(0, Number(e.target.value) || 0))
              }
            />
          </Field>
        </div>
      </div>

      <div className="rounded-lg border border-border p-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold">Verification</div>
            <div className="text-xs text-muted-foreground">
              Assign an unverified role on join; members click a button to receive the verified role. Run{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">/verification_panel</code> in Discord to post the button.
              Use channel permissions so only verified users see the rest of the server.
            </div>
          </div>
          <Switch checked={cfg.verification.enabled} onCheckedChange={(v) => updateVerification("enabled", v)} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Unverified role (optional)" hint="Given on join when verification is enabled. Leave empty if you only add verified manually.">
            <Select
              value={cfg.verification.unverified_role_id || "__none__"}
              onValueChange={(v) =>
                updateVerification("unverified_role_id", v === "__none__" ? "" : v)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {roleOptions.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    @{r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Verified role (required)" hint="Granted when the user presses Verify on your panel message.">
            <Select
              value={cfg.verification.verified_role_id || "__none__"}
              onValueChange={(v) =>
                updateVerification("verified_role_id", v === "__none__" ? "" : v)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Pick role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {roleOptions.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    @{r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <div className="text-sm font-medium">Hold autorole until verified</div>
            <div className="text-xs text-muted-foreground">
              When on, dashboard autorole is applied only after the member verifies (recommended).
            </div>
          </div>
          <Switch
            checked={cfg.verification.skip_autorole_until_verified}
            onCheckedChange={(v) => updateVerification("skip_autorole_until_verified", v)}
          />
        </div>
      </div>

      <div className="rounded-lg border border-border p-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold">Anti-nuke</div>
            <div className="text-xs text-muted-foreground">React when one executor deletes too many channels/roles or bans too many members.</div>
          </div>
          <Switch checked={cfg.anti_nuke.enabled} onCheckedChange={(v) => updateNuke("enabled", v)} />
        </div>
        <div className="grid md:grid-cols-5 gap-4">
          <Field label="Window seconds">
            <Input type="number" min={10} max={600} value={cfg.anti_nuke.window_seconds} onChange={(e) => updateNuke("window_seconds", Math.max(10, Number(e.target.value) || 60))} />
          </Field>
          <Field label="Channel deletes">
            <Input type="number" min={1} max={50} value={cfg.anti_nuke.max_channel_deletes} onChange={(e) => updateNuke("max_channel_deletes", Math.max(1, Number(e.target.value) || 3))} />
          </Field>
          <Field label="Role deletes">
            <Input type="number" min={1} max={50} value={cfg.anti_nuke.max_role_deletes} onChange={(e) => updateNuke("max_role_deletes", Math.max(1, Number(e.target.value) || 3))} />
          </Field>
          <Field label="Bans">
            <Input type="number" min={1} max={50} value={cfg.anti_nuke.max_bans} onChange={(e) => updateNuke("max_bans", Math.max(1, Number(e.target.value) || 5))} />
          </Field>
          <Field label="Action">
            <NukeActionSelect value={cfg.anti_nuke.action} onChange={(v) => updateNuke("action", v)} />
          </Field>
        </div>
      </div>

      <div className="rounded-lg border border-border p-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold">Anti-link</div>
            <div className="text-xs text-muted-foreground">Block unauthorized website links from being sent.</div>
          </div>
          <Switch checked={cfg.anti_link?.enabled || false} onCheckedChange={(v) => updateLink("enabled", v)} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Action">
            <ActionSelect value={cfg.anti_link?.action || "delete"} onChange={(v) => updateLink("action", v)} />
          </Field>
          <Field label="Whitelisted domains" hint="One domain per line. Safe domains that are allowed.">
            <Textarea
              rows={4}
              value={(cfg.anti_link?.whitelisted_domains || []).join("\n")}
              onChange={(e) => updateLink("whitelisted_domains", parseDomains(e.target.value, 100))}
              placeholder="youtube.com\ntwitch.tv"
            />
          </Field>
        </div>
      </div>

      <div className="rounded-lg border border-border p-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold">Anti-phishing & Anti-scam</div>
            <div className="text-xs text-muted-foreground">Block fake Discord Nitro, Steam gift scams, and known phishing links.</div>
          </div>
          <Switch checked={cfg.anti_phishing !== false} onCheckedChange={(v) => update("anti_phishing", v)} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Action" hint="Action to take when a phishing/scam link is detected (recommended: Timeout or Ban).">
            <ActionSelect value={cfg.anti_phishing_action || "timeout"} onChange={(v) => update("anti_phishing_action", v)} allowDelete={false} />
          </Field>
          <Field label="Scam protection active" hint="Real-time scan against malicious domains and alt-spelling scams.">
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
              Automatically deletes scam links and punishes executors using the selected action.
            </div>
          </Field>
        </div>
      </div>
    </Section>
  );
}
